import { Router, useRouter } from "next/router";
import React from "react";

import { useExperience } from "@/context/ExperienceContext";
import { useExitModal } from "@/hooks/useExitModal";

import { useParseLocalizedPathname } from "./useParseLocalizedPathname";

const targetProviderState = {
  isScrolling: false
};

export { targetProviderState };

const getMouseEventOpensInNewWindow = (event: React.MouseEvent) => {
  const target = event.currentTarget.getAttribute("target");

  return (
    (target && target !== "_self") ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    (event.nativeEvent && event.nativeEvent.which === 2)
  );
};

const waitForNavigationComplete = () => {
  return new Promise((resolve) => {
    const handleComplete = (url: string) => {
      Router.events.off("routeChangeComplete", handleComplete);
      resolve(url);
    };

    Router.events.on("routeChangeComplete", handleComplete);
  });
};

export const useTargetProvider = () => {
  const { openExitModal, openExitModalForLink, getIsExternalLink } = useExitModal();
  const { host } = useExperience();
  const { push, asPath } = useRouter();
  const { parseLocalizedPathname } = useParseLocalizedPathname();
  const asPathParsed = React.useMemo(
    () => parseLocalizedPathname(asPath),
    [asPath, parseLocalizedPathname]
  );

  const handleClick = React.useCallback(
    async (e: React.MouseEvent) => {
      const href = e.currentTarget.getAttribute("href");

      if (e.currentTarget instanceof HTMLAnchorElement) {
        if (e.currentTarget.hasAttribute("data-exiting-link")) {
          openExitModal(false);
        } else if (e.currentTarget.hasAttribute("data-skip-exit-modal")) {
          // Explicitly opted out of the interstitial - let the browser navigate natively.
          return;
        } else if (href && e.currentTarget.hasAttribute("data-force-exit-modal")) {
          e.preventDefault();
          openExitModalForLink(
            href,
            e.currentTarget.getAttribute("data-exit-modal-reason") || undefined
          );
        } else if (href && getIsExternalLink(href)) {
          e.preventDefault();
          openExitModal(true);
        } else if (href?.startsWith("/") && !getMouseEventOpensInNewWindow(e)) {
          e.preventDefault();
          const scroll = e.currentTarget.closest("[data-scroll]")?.getAttribute("data-scroll");

          if (!scroll) {
            push(href);
          } else {
            push(href, undefined, { scroll: false });

            const element = document.getElementById(scroll);

            if (scroll !== "false" && element && element.getBoundingClientRect().top <= 0) {
              targetProviderState.isScrolling = true;
              await waitForNavigationComplete();
              document.getElementById(scroll)?.scrollIntoView();
              // Wait a moment for the scrolling to resolve as it sometimes
              // runs asynchronously
              setTimeout(() => {
                targetProviderState.isScrolling = false;
              }, 100);
            }
          }
        }
      }
    },
    [getIsExternalLink, openExitModal, openExitModalForLink, push]
  );

  const resolveTarget = React.useCallback(
    (hrefInitial?: string) => {
      if (!hrefInitial) {
        return {};
      }

      const isRelativeHref = hrefInitial?.startsWith("/");
      const isHashedHref = hrefInitial?.startsWith("#");
      const atlasTargetHost = (() => {
        try {
          return new URL(hrefInitial).hostname;
        } catch {
          return undefined;
        }
      })();
      const shouldForceSelfTarget =
        host?.includes("connect.atlas.modernatx.com") &&
        atlasTargetHost?.includes("atlas.modernatx.com");
      const target = isRelativeHref || isHashedHref || shouldForceSelfTarget ? "_self" : "_blank";

      if (!isRelativeHref || hrefInitial?.startsWith("/assets/")) {
        return { href: hrefInitial, target };
      }

      const hrefParsed = parseLocalizedPathname(hrefInitial);

      let href = "";

      // If the href already has a country basename, use it. If it doesn't but the
      // current URL does, include that URL's country basename on the target href
      if (hrefParsed.country) {
        href = `${href}/${hrefParsed.country}`;
      } else if (asPathParsed.country) {
        href = `${href}/${asPathParsed.country}`;
      }

      // If the href already has a language basename, use it. If it doesn't but the
      // current URL does, include that URL's country basename on the target href only
      // if the target href is pointing to the country experience the user is on
      if (hrefParsed.language) {
        href = `${href}/${hrefParsed.language}`;
      } else if (
        asPathParsed.language &&
        (hrefParsed.country === asPathParsed.country ||
          (hrefParsed.country === undefined && asPathParsed.country === "us"))
      ) {
        href = `${href}/${asPathParsed.language}`;
      }

      href = `${href}${hrefParsed.pathname}`;

      return { href, target };
    },
    [asPathParsed, host, parseLocalizedPathname]
  );

  return { handleClick, resolveTarget };
};
